import { ROLE_CENSORED_TOKEN, USER_AVATAR_TYPE_OLD } from "@/utils/variables";
import { Profile } from "@/interfaces/User/Profile";
import { useRoleStore } from "@/store/role.ts";
import { getRandomCensoredImage } from "@/utils/censorHelper.ts";

export const getMediaUrl = (dir: string, name: string): string => {
  return `${import.meta.env.VITE_MEDIA_CONTENT_HOST}/${dir}/${name}`;
};

const getThumbnailUrl = (dir: string, name: string, options: Record<string, any> = {}) => {
  options.quality = 85;
  options.sharpen = 1;

  if (useRoleStore().hasRole(ROLE_CENSORED_TOKEN)) {
    return getRandomCensoredImage(options);
  }

  const props = [];

  for (const [key, value] of Object.entries(options)) {
    props.push(`${key}=${value}`);
  }

  const size_dir = `${options.width}_${options.height}`;
  const fit = options.fit || "";
  return `${import.meta.env.VITE_MEDIA_CONTENT_HOST}/cache/thumbs/${size_dir}/${name}?img_dir=${dir}&width=${options.width}&height=${options.height}&fit=${fit}`;
};

export const getProfileAvatar = (profile: Profile) => {
  if (profile.avatar === null) {
    return require("@/assets/images/user_profile/default_avatar.svg");
  }

  return profile.avatar === USER_AVATAR_TYPE_OLD ? getMediaUrl("upload/avatars", `${profile.id}.svg`) : getNewAvatar(`${profile.avatar}.svg`);
};

export const getNewAvatar = (name: string) => {
  return getMediaUrl("upload/avatars/new", name);
};

export const getVideoTimelinePreview = (name: string) => {
  if (useRoleStore().hasRole(ROLE_CENSORED_TOKEN)) {
    return getRandomCensoredImage({ width: 120, height: 90, fit: "cover" });
  }

  return getMediaUrl("content/images_mysql/video_timeline_previews", name);
};

export const getWhoWeAreVideoUrl = (lang: string) => {
  return getMediaUrl(
    "content/videos/about_us/recoded",
    `,160/team_${lang}.mp4,240/team_${lang}.mp4,360/team_${lang}.mp4,480/team_${lang}.mp4,720/team_${lang}.mp4,1080/team_${lang}.mp4,.urlset/master.m3u8`,
  );
};

export const getBehindTheScenesUrl = (): string => {
  return getMediaUrl(
    "content/videos/about_us/recoded",
    `,160/bts.mp4,240/bts.mp4,360/bts.mp4,480/bts.mp4,720/bts.mp4,1080/bts.mp4,.urlset/master.m3u8`,
  );
};

export const getBehindTheScenesUrlV2 = (): string => {
  return getMediaUrl(
    "content/videos/about_us/recoded",
    `,160/bts_v2.mp4,240/bts_v2.mp4,360/bts_v2.mp4,480/bts_v2.mp4,720/bts_v2.mp4,1080/bts_v2.mp4,.urlset/master.m3u8`,
  );
};

export const getVideoPoster = (name: string) => {
  return getThumbnailUrl("content/images_mysql/images_videothumbnails/backup", name, { width: 900, height: 500, fit: "cover" });
};

export const getVideoPosterMini = (name: string) => {
  return getThumbnailUrl("content/images_mysql/images_videothumbnails/backup", name, { width: 370, height: 206, fit: "cover" });
};

export const getPublicVideoPoster = (name: string) => {
  return getThumbnailUrl("content/images_mysql/images_videothumbnails_public/backup", name, { width: 900, height: 500, fit: "cover" });
};

export const getVideoThumbnail = (name: string) => {
  return getThumbnailUrl("content/images_mysql/images_videothumbnails/backup", name, { width: 400, height: 490, fit: "cover" });
};

export const getFreeareaVideoPoster = (name: string) => {
  return getThumbnailUrl("upload/CMS/freearea", name, { width: 900, height: 500, fit: "cover" });
};

export const getFreeareaVideoPosterBig = (name: string) => {
  return getThumbnailUrl("upload/CMS/freearea", name, { width: 1280, height: 720, fit: "cover" });
};

export const getGalleryImageThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Images/backup", name, { width: 400, height: 400, fit: "cover" });
};

export const getGalleryImageMediumThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Images/backup", name, { width: 600, height: 600, fit: "cover" });
};

export const getGalleryImage = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Images/backup", name, { width: 1920, height: 1080 });
};

export const getUpdateFullThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Recent_Image/backup", name, { width: 800, height: 450 });
};

export const getUpdateMiniThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Recent_Image/backup", name, { width: 660, height: 440 });
};

export const getUpdateBlogThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Recent_Image/backup", name, { width: 380, height: 1500 });
};

export const getPublicModelThumb = (name: string, width = 510, height = 660, adaptive = true) => {
  const options: Record<string, any> = { width, height };

  if (!adaptive) {
    options.fit = "cover";
  }

  return getThumbnailUrl("content/images_mysql/Model_Cover_Image_Unsubscribed/backup", name, options);
};

export const getModelThumb = (name: string, width = 510, height = 660) => {
  const options = { width, height, fit: "cover" };

  return getThumbnailUrl("content/images_mysql/Model_Cover_Image/backup", name, options);
};

export const getGalleryThumb = (name: string, width = 510, height = 660) => {
  const options = { width, height, fit: "cover" };

  return getThumbnailUrl("content/images_mysql/Shoot_Cover", name, options);
};

export const getModelThumbMini = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Cover_Image/backup", name, { width: 270, height: 360 });
};

export const getPlaydateCameraThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Playdate_Image/backup", name, { width: 340, height: 440, fit: "cover" });
};

export const getPublicPlaydateItemThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Playdate_Image_Page/backup", name, { width: 450, height: 1500 });
};

export const getCustomNewAndUpcomingThumb = (name: string) => {
  const options = { width: 510, height: 660, fit: "cover" };

  return getThumbnailUrl("upload/CMS/new_upcoming", name, options);
};

export const getBlogPostThumbSmall = (dir: string, name: string) => {
  const options = { width: 450, height: 360, fit: "cover" };

  return getThumbnailUrl(dir, name, options);
};

export const getBlogPostThumbLarge = (dir: string, name: string) => {
  const options = { width: 900, height: 675, fit: "cover" };

  return getThumbnailUrl(dir, name, options);
};

export const getPollHeaderImage = (name: string) => {
  return getMediaUrl("content/images_mysql/images_polls/backup", name);
};

export const getDocumentUrl = (name: string) => {
  return getMediaUrl("content/documents", name);
};

export const getBlogPostVideoMedia = (name: string) => {
  return getMediaUrl("upload/CMS/embed_videos", name);
};
